import React, { memo, useMemo } from 'react'
import PropTypes from 'prop-types'

import { getNames, getTitleError, getImageError } from './errors'

import Subject from './Subject'
import Data from './Data'
import Control from './Control'
import Selector from './Selector'
import View from './View'
import Error from './Error'

import './Editor.scss'

const CLASS = 'insights-portal-editor'

const EditorInternal = props => {
  const className = props.className || CLASS

  const editor = useMemo(() => props.editor || {}, [props.editor])

  const uid = useMemo(() => editor?.data?.uid, [editor?.data?.uid])

  const subjects = useMemo(() => props.subjects || {}, [props.subjects])

  const names = useMemo(() => getNames(subjects, uid), [subjects, uid])

  const onCheck = (type, value) => {
    if ('title' === type) {
      const error = getTitleError(value || '', names) || null
      props.onEditorChange?.({ error })
      return error
    } else if ('image' === type) {
      const error = getImageError(value) || null
      props.onEditorChange?.({ error })
      return error
    }
    return null
  }

  const onSave = () => {
    if (props.onEditorChange) {
      props.onEditorChange({ action: 'save' })
    }
  }

  const onCancel = () => {
    if (props.onEditorChange) props.onEditorChange({})
  }

  const onAdd = () => {
    props.onEditorChange?.({ type: 'edit', itemIndex: props.itemIndex, item: props.item })
  }

  switch (props.rowType) {
    case 'view': {
      return (
        <View
          style={props.style}
          key={`editor-${props.rowType}`}
          editor={editor}
          showImage={props.showImage}
          onEditorChange={props.onEditorChange}
          onImageUpload={props.onImageUpload}
          onCheck={onCheck}
        />
      )
    }
    case 'subject': {
      return (
        <Subject
          style={props.style}
          key={`editor-${props.rowType}`}
          editor={editor}
          subjectType={props.subjectType}
          onEditorChange={props.onEditorChange}
          onCheck={onCheck}
        >
          {props.children}
        </Subject>
      )
    }
    case 'data': {
      return (
        <Data
          key={`editor-${props.rowType}`}
          className={`${className}-data`}
          rowType={'data'}
          editor={editor}
          readOnly={props.wait}
          caption={props.caption}
          attributeIndex={props.attributeIndex}
          onEditorChange={props.onEditorChange}
          onCheck={onCheck}
        />
      )
    }
    case 'control': {
      return (
        <Control
          key={`editor-${props.rowType}`}
          editor={editor}
          readOnly={props.wait}
          subjectType={props.subjectType}
          onSave={onSave}
          onCancel={onCancel}
          onAdd={onAdd}
          onEditorChange={props.onEditorChange}
        />
      )
    }
    case 'selector': {
      return (
        <Selector
          key={`editor-${props.rowType}`}
          subjectType={props.subjectType}
          subjects={props.subjects}
          onItemAdd={props.onItemAdd}
        />
      )
    }
    case 'title': {
      return <div className={`${className}-title`}>{'edit' === props.editor.type ? props.text : null}</div>
    }
    case 'error': {
      return <Error key={`editor-${props.rowType}`} editor={editor} />
    }
    default: {
      return null
    }
  }
}

EditorInternal.propTypes = {
  className: PropTypes.string,
  children: PropTypes.any,
  style: PropTypes.object,
  rowType: PropTypes.string,
  subjects: PropTypes.object,
  editor: PropTypes.object,
  // attribute: PropTypes.string,
  attributeIndex: PropTypes.number,
  itemIndex: PropTypes.number,
  item: PropTypes.object,
  subjectType: PropTypes.string,
  wait: PropTypes.any,
  text: PropTypes.any,
  showImage: PropTypes.any,
  // metric: PropTypes.object,
  // defaultMetric: PropTypes.object,
  caption: PropTypes.object,
  onAdd: PropTypes.func,
  onEditorChange: PropTypes.func,
  onItemAdd: PropTypes.func,
  onImageUpload: PropTypes.func
}

const arePropsEqual = (pre, cur) =>
  pre.editor === cur.editor &&
  pre.itemIndex === cur.itemIndex &&
  pre.wait === cur.wait &&
  pre.text === cur.text &&
  pre.showImage === cur.showImage &&
  pre.subjects === cur.subjects &&
  pre.onEditorChange === cur.onEditorChange

const Editor = memo(EditorInternal, arePropsEqual)

Editor.displayName = 'Editor'

Editor.propTypes = {
  className: PropTypes.string,
  children: PropTypes.any,
  style: PropTypes.object,
  rowType: PropTypes.string,
  subjects: PropTypes.object,
  editor: PropTypes.object,
  // attribute: PropTypes.string,
  attributeIndex: PropTypes.number,
  itemIndex: PropTypes.number,
  subjectType: PropTypes.string,
  text: PropTypes.any,
  showImage: PropTypes.any,
  // metric: PropTypes.object,
  // defaultMetric: PropTypes.object,
  caption: PropTypes.object,
  onAdd: PropTypes.func,
  onEditorChange: PropTypes.func,
  onItemAdd: PropTypes.func,
  onImageUpload: PropTypes.func
}

Editor.defaultProps = {
  editor: { type: 'add' },
  subjectType: 'concept',
  showImage: true
}

export default Editor
